import React, { useState, useEffect, useRef } from 'react';
import './AIJobCreationChat.css';
import useCurrentRestaurant from '../../../hooks/useCurrentRestaurant';
import useFetchAllLocationsCompanies from '../../../hooks/company/useFetchAllLocationsCompanies';
import { useAIJobCreationChat } from '../../../hooks/ai/useAIJobCreationChat';

// Function to format AI messages with proper HTML
const formatAIMessage = (content) => {
  if (!content) return '';
  
  return content
    // First, convert line breaks to <br> tags
    .replace(/\n/g, '<br>')
    // Convert **text** to <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Handle bullet points that are on the same line - split them first
    .replace(/• ([^•]+) •/g, '• $1<br>• ')
    // Convert individual bullet points to list items
    .replace(/• ([^<]+?)(?=<br>|$)/g, '<li>$1</li>')
    // Wrap consecutive <li> elements in <ul>
    .replace(/(<li>.*?<\/li>)(<br><li>.*?<\/li>)*/g, (match) => {
      return '<ul>' + match.replace(/<br>/g, '') + '</ul>';
    })
    // Clean up any remaining <br> tags before <ul>
    .replace(/<br><ul>/g, '<ul>')
    // Clean up any remaining <br> tags after </ul>
    .replace(/<\/ul><br>/g, '</ul>')
    // Add extra spacing between sections
    .replace(/<br><br>/g, '<br><br>')
    // Clean up multiple consecutive <br> tags
    .replace(/(<br>){3,}/g, '<br><br>');
};

const AIJobCreationChat = ({ onJobCreated, onClose }) => {
  const [selectedLocationId, setSelectedLocationId] = useState('');
  
  const { restaurantId } = useCurrentRestaurant();
  const { locations, isLoading: locationsLoading, error: locationsError } = useFetchAllLocationsCompanies();
  
  const messagesEndRef = useRef(null);
  
  const {
    messages,
    inputMessage,
    isLoading,
    extractedData,
    showFormPreview,
    setInputMessage,
    setShowFormPreview,
    sendMessage,
    createJobFromAI,
    initializeChat
  } = useAIJobCreationChat(restaurantId, selectedLocationId);

  useEffect(() => {
    if (!locationsLoading) {
      initializeChat(locations);
    }
  }, [initializeChat, locations, locationsLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    sendMessage();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateJob = () => {
    createJobFromAI(selectedLocationId, onJobCreated);
  };

  const getLocationName = (locationId) => {
    const location = locations?.find(loc => loc.locationId === parseInt(locationId));
    return location ? location.address : 'Ubicación no encontrada';
  };

  return (
    <div className="ai-job-creation-chat">
      <div className="chat-header">
        <h3>🤖 Crear Trabajo con IA</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="chat-messages">
        {messages.map((message) => {
          return (
            <div key={message.id} className={`message ${message.type}`}>
              <div className="message-content">
                {message.type === 'ai' ? (
                  <div 
                    className="ai-message-formatted"
                    dangerouslySetInnerHTML={{ __html: formatAIMessage(message.content) }}
                  />
                ) : (
                  <p>{message.content || 'Sin contenido'}</p>
                )}
                {message.type === 'ai' && (
                  <div className="debug-info">
                    {message.debugInfo ? (
                      <>
                        <span className={`debug-badge ${message.debugInfo.source === 'LLM' ? 'llm-badge' : 'regex-badge'}`}>
                          {message.debugInfo.source === 'LLM' ? '🤖 LLM' : '🔧 REGEX'}
                        </span>
                        <span className="debug-timestamp">
                          {new Date(message.debugInfo.timestamp).toLocaleTimeString()}
                        </span>
                        {message.openaiStatus && (
                          <span className={`openai-status ${message.openaiStatus.available ? 'openai-available' : 'openai-unavailable'}`}>
                            {message.openaiStatus.available ? '✅ OpenAI OK' : '⚠️ OpenAI Quota Exceeded'}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="debug-badge no-debug-badge">
                        ❓ UNKNOWN
                      </span>
                    )}
                  </div>
                )}
                {message.type === 'ai' && message.extractedData && Object.keys(message.extractedData).length > 0 && (
                  <div className="extracted-data-preview">
                    <h4>📋 Datos extraídos:</h4>
                    <div className="data-grid">
                      {message.extractedData.position && (
                        <div className="data-item">
                          <strong>Posición:</strong> {message.extractedData.position}
                        </div>
                      )}
                      {message.extractedData.schedule && (
                        <div className="data-item">
                          <strong>Horario:</strong> {message.extractedData.schedule}
                        </div>
                      )}
                      {message.extractedData.salary && (
                        <div className="data-item">
                          <strong>Salario:</strong> ${message.extractedData.salary.toLocaleString()}
                        </div>
                      )}
                      {message.extractedData.vacancies && (
                        <div className="data-item">
                          <strong>Vacantes:</strong> {message.extractedData.vacancies}
                        </div>
                      )}
                      {message.extractedData.contract && (
                        <div className="data-item">
                          <strong>Contrato:</strong> {message.extractedData.contract}
                        </div>
                      )}
                      {message.extractedData.yearsOfExperience !== undefined && (
                        <div className="data-item">
                          <strong>Experiencia:</strong> {message.extractedData.yearsOfExperience} años
                        </div>
                      )}
                      {message.extractedData.period && (
                        <div className="data-item">
                          <strong>Período:</strong> {message.extractedData.period}
                        </div>
                      )}
                      {message.extractedData.tips !== undefined && (
                        <div className="data-item">
                          <strong>Propinas:</strong> {message.extractedData.tips ? 'Sí' : 'No'}
                        </div>
                      )}
                      {message.extractedData.description && (
                        <div className="data-item">
                          <strong>Descripción:</strong> {message.extractedData.description}
                        </div>
                      )}
                      {message.extractedData.functions && (
                        <div className="data-item">
                          <strong>Funciones:</strong> {message.extractedData.functions}
                        </div>
                      )}
                      {message.extractedData.requirements && (
                        <div className="data-item">
                          <strong>Requisitos:</strong> {message.extractedData.requirements}
                        </div>
                      )}
                      {message.extractedData.questions && message.extractedData.questions.length > 0 && (
                        <div className="data-item">
                          <strong>Preguntas:</strong> {message.extractedData.questions.length} pregunta(s)
                        </div>
                      )}
                      {message.extractedData.location && (
                        <div className="data-item">
                          <strong>Ubicación:</strong> {message.extractedData.location}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString('es-CL', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          );
        })}
        
        {isLoading && (
          <div className="message ai">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {showFormPreview && (
        <div className="form-preview">
          <h4>📝 Vista previa del trabajo</h4>
          <div className="preview-content">
            <div className="preview-section">
              <h5>📋 Información básica</h5>
              <div className="field-row">
                <span className="field-label">👨‍💼 Posición:</span>
                <span className={`field-value ${extractedData.position ? 'completed' : 'missing'}`}>
                  {extractedData.position || '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">📅 Horario:</span>
                <span className={`field-value ${extractedData.schedule ? 'completed' : 'missing'}`}>
                  {extractedData.schedule || '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">📋 Contrato:</span>
                <span className={`field-value ${extractedData.contract ? 'completed' : 'missing'}`}>
                  {extractedData.contract || '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">💰 Salario:</span>
                <span className={`field-value ${extractedData.salary ? 'completed' : 'missing'}`}>
                  {extractedData.salary ? `$${extractedData.salary.toLocaleString()}` : '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">💸 Propinas:</span>
                <span className={`field-value ${extractedData.tips !== undefined ? 'completed' : 'missing'}`}>
                  {extractedData.tips !== undefined ? (extractedData.tips ? 'Sí' : 'No') : '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">👥 Vacantes:</span>
                <span className={`field-value ${extractedData.vacancies ? 'completed' : 'missing'}`}>
                  {extractedData.vacancies || '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">🎯 Experiencia:</span>
                <span className={`field-value ${extractedData.yearsOfExperience !== undefined ? 'completed' : 'missing'}`}>
                  {extractedData.yearsOfExperience !== undefined ? `${extractedData.yearsOfExperience} años` : '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                <span className="field-label">⏰ Período:</span>
                <span className={`field-value ${extractedData.period ? 'completed' : 'missing'}`}>
                  {extractedData.period || '❌ Falta especificar'}
                </span>
              </div>
              <div className="field-row">
                {/* <span className="field-label">📍 Ubicación:</span>
                <span className={`field-value ${extractedData.location ? 'completed' : 'missing'}`}>
                  {extractedData.location || '❌ Falta especificar'}
                </span> */}
              </div>
            </div>
            
            <div className="preview-section">
              <h5>📝 Descripción</h5>
              <div className="field-row">
                <span className={`field-value ${extractedData.description ? 'completed' : 'missing'}`}>
                  {extractedData.description || '❌ Falta especificar'}
                </span>
              </div>
            </div>
            
            <div className="preview-section">
              <h5>✅ Requisitos</h5>
              <div className="field-row">
                <span className={`field-value ${extractedData.requirements ? 'completed' : 'missing'}`}>
                  {extractedData.requirements || '❌ Falta especificar'}
                </span>
              </div>
            </div>
            
            <div className="preview-section">
              <h5>⚙️ Funciones</h5>
              <div className="field-row">
                <span className={`field-value ${extractedData.functions ? 'completed' : 'missing'}`}>
                  {extractedData.functions || '❌ Falta especificar'}
                </span>
              </div>
            </div>

            <div className="preview-section">
              <h5>❓ Preguntas para candidatos</h5>
              <div className="field-row">
                <span className={`field-value ${extractedData.questions && extractedData.questions.length > 0 ? 'completed' : 'missing'}`}>
                  {extractedData.questions && extractedData.questions.length > 0 ? (
                    <ul>
                      {extractedData.questions.map((q, index) => (
                        <li key={index}>{q.question}</li>
                      ))}
                    </ul>
                  ) : '❌ Falta especificar'}
                </span>
              </div>
            </div>

            <div className="location-selection">
              <label htmlFor="location-select">Selecciona la ubicación:</label>
              {locations && locations.length > 0 ? (
                <select 
                  id="location-select"
                  value={selectedLocationId} 
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="location-select"
                >
                  <option value="">Selecciona una ubicación</option>
                  {locations.map((location) => (
                    <option key={location.locationId} value={location.locationId}>
                      {location.address}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="no-locations-warning">
                  <p>⚠️ No hay ubicaciones disponibles para este restaurante.</p>
                  <p>Por favor, agrega al menos una ubicación en la configuración del restaurante antes de crear trabajos.</p>
                </div>
              )}
            </div>

            <div className="preview-actions">
              <button 
                className="create-job-btn"
                onClick={handleCreateJob}
                disabled={isLoading || !selectedLocationId || !locations || locations.length === 0}
              >
                {isLoading ? 'Creando...' : 'Crear Trabajo'}
              </button>
              <button 
                className="edit-form-btn"
                onClick={() => setShowFormPreview(false)}
              >
                Seguir editando
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-input">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Describe el trabajo que necesitas... (ej: Necesito un chef para mi restaurante, tiempo completo, $3000, 3 años de experiencia)"
          disabled={isLoading}
          rows="3"
        />
        <button 
          onClick={handleSendMessage} 
          disabled={isLoading || !inputMessage.trim()}
          className="send-btn"
        >
          Enviar
        </button>
      </div>
    </div>
  );
};

export default AIJobCreationChat;
